"use client"

import { useTranslations } from "next-intl"
import { Download } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useBrowser, useDevice } from "@/hooks/use-browser"
import { resolveInstallTarget } from "@/lib/install-targets"

/**
 * Highlighted, device-aware "install donista" CTA for the marketing nav.
 * Desktop → the right extension store; mobile → the right app store.
 *
 * Uses `useDevice()`/`useBrowser()` (both `useSyncExternalStore` with a stable
 * `desktop`/`chrome` server snapshot), so SSR + first render show the extension
 * label and React patches post-hydration. The href is an external store URL, so
 * this is a plain `<a target="_blank">`, NOT the locale-aware `Link`.
 */
export function InstallCtaButton({
  className,
  size = "default",
  variant = "glass",
}: {
  className?: string
  size?: "sm" | "default"
  variant?: "glass" | "default"
}) {
  const t = useTranslations("common")
  const device = useDevice()
  const browser = useBrowser() as "chrome" | "firefox" | "safari"
  const { kind, url } = resolveInstallTarget({ device, browser })

  const isApp = kind === "appStore" || kind === "googlePlay"
  const label = isApp ? t("nav.getApp") : t("nav.installExtension")

  return (
    <Button asChild size={size} variant={variant} className={cn("rounded-xl gap-1.5", className)}>
      <a href={url} target="_blank" rel="noopener noreferrer">
        <Download className="size-4" />
        {label}
      </a>
    </Button>
  )
}
